import { useEffect, useMemo, useState, type FormEvent } from "react";
import { api } from "@/lib/api";
import { brandColors as colors } from "@/lib/palette";
import { fonts } from "@/lib/fonts";
import { formatInteger, formatMoney } from "@/lib/format";
import {
  AdminTableControls,
  AdminTableFilterSelect,
  SortableTh,
  useAdminTable,
} from "@/components/admin/AdminTableControls";
import AdminFormHeader from "@/components/admin/AdminFormHeader";

type Product = {
  id: string;
  slug: string;
  name: string;
  brand: string;
  description: string;
  categoryId: string;
  priceCents: number;
  inventory: number;
  weightOz: number | null;
  tag: string | null;
  featured: boolean;
  sortOrder: number;
  status: "draft" | "published";
};
type Category = { id: string; name: string };
type ProductSortKey = "name" | "brand" | "price" | "stock" | "status";

type FormState = {
  name: string;
  brand: string;
  description: string;
  categoryId: string;
  priceDollars: string;
  inventory: string;
  weightOz: string;
  tag: string;
  sortOrder: string;
  slug: string;
  featured: boolean;
  status: "draft" | "published";
};

const empty: FormState = {
  name: "",
  brand: "",
  description: "",
  categoryId: "",
  priceDollars: "",
  inventory: "0",
  weightOz: "",
  tag: "",
  sortOrder: "0",
  slug: "",
  featured: false,
  status: "draft",
};

function toForm(p: Product): FormState {
  return {
    name: p.name,
    brand: p.brand,
    description: p.description,
    categoryId: p.categoryId,
    priceDollars: (p.priceCents / 100).toString(),
    inventory: p.inventory.toString(),
    weightOz: p.weightOz?.toString() ?? "",
    tag: p.tag ?? "",
    sortOrder: p.sortOrder.toString(),
    slug: p.slug,
    featured: p.featured,
    status: p.status,
  };
}

function productSearchText(product: Product) {
  return [
    product.name,
    product.slug,
    product.brand,
    product.description,
    product.tag,
    product.status,
    formatMoney(product.priceCents),
  ]
    .filter(Boolean)
    .join(" ");
}

const productSorters: Record<ProductSortKey, (row: Product) => string | number> = {
  name: (row) => row.name,
  brand: (row) => row.brand,
  price: (row) => row.priceCents,
  stock: (row) => row.inventory,
  status: (row) => row.status,
};

export default function AdminProducts() {
  const [list, setList] = useState<Product[]>([]);
  const [cats, setCats] = useState<Category[]>([]);
  const [editing, setEditing] = useState<Product | "new" | null>(null);
  const [form, setForm] = useState<FormState>(empty);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [categoryFilter, setCategoryFilter] = useState("all");

  async function refresh() {
    const r = await api.get<{ products: Product[] }>("/api/admin/products");
    setList(r.products);
  }

  useEffect(() => {
    Promise.all([
      refresh(),
      api.get<{ categories: Category[] }>("/api/admin/categories").then((r) => setCats(r.categories)),
    ]).finally(() => setLoaded(true));
  }, []);

  function startNew() {
    setForm({ ...empty, categoryId: cats[0]?.id ?? "" });
    setEditing("new");
    setErr(null);
  }
  function startEdit(p: Product) {
    setForm(toForm(p));
    setEditing(p);
    setErr(null);
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    const payload = {
      name: form.name,
      brand: form.brand,
      description: form.description,
      categoryId: form.categoryId,
      priceCents: Math.round(parseFloat(form.priceDollars || "0") * 100),
      inventory: parseInt(form.inventory || "0", 10),
      weightOz: form.weightOz ? parseFloat(form.weightOz) : null,
      tag: form.tag || null,
      sortOrder: parseInt(form.sortOrder || "0", 10),
      slug: form.slug || undefined,
      featured: form.featured,
      status: form.status,
    };
    try {
      if (editing === "new") await api.post("/api/admin/products", payload);
      else if (editing) await api.put(`/api/admin/products/${editing.id}`, payload);
      await refresh();
      setEditing(null);
    } catch (e2) {
      setErr(e2 instanceof Error ? e2.message : "Save failed");
    } finally {
      setBusy(false);
    }
  }

  async function remove(p: Product) {
    if (!confirm(`Delete "${p.name}"? This cannot be undone.`)) return;
    await api.del(`/api/admin/products/${p.id}`);
    await refresh();
  }

  const categoryOptions = useMemo(
    () => [
      { value: "all", label: "All categories" },
      ...cats.map((cat) => ({ value: cat.id, label: cat.name })),
    ],
    [cats],
  );
  const filters = useMemo(
    () => [
      (row: Product) => statusFilter === "all" || row.status === statusFilter,
      (row: Product) => categoryFilter === "all" || row.categoryId === categoryFilter,
    ],
    [categoryFilter, statusFilter],
  );
  const {
    rows: tableRows,
    sort,
    requestSort,
  } = useAdminTable<Product, ProductSortKey>({
    rows: list,
    search,
    initialSort: { key: "name", direction: "asc" },
    sorters: productSorters,
    searchText: productSearchText,
    filters,
  });

  return (
    <section>
      <style>{css}</style>
      <div className="hdr">
        <h1 className="aflt-admin-title">Products</h1>
        {!editing && (
          <button type="button" className="btn-woven" onClick={startNew}>
            + New product
          </button>
        )}
      </div>

      {editing ? (
        <form onSubmit={submit} className="paper-card">
          <AdminFormHeader
            title={editing === "new" ? "New product" : editing.name}
            eyebrow={editing === "new" ? "Create product" : "Edit product"}
            backLabel="Back to products"
            onBack={() => setEditing(null)}
          />
          <div className="grid2">
            <Field label="Name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} required />
            <Field label="Brand" value={form.brand} onChange={(v) => setForm({ ...form, brand: v })} required />
          </div>
          <Field label="Description" value={form.description} onChange={(v) => setForm({ ...form, description: v })} textarea required />
          <div className="grid2">
            <label className="fld">
              <span>Category</span>
              <select value={form.categoryId} onChange={(e) => setForm({ ...form, categoryId: e.target.value })} required>
                <option value="">Select…</option>
                {cats.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </label>
            <Field label="Price (USD)" value={form.priceDollars} onChange={(v) => setForm({ ...form, priceDollars: v })} required />
          </div>
          <div className="grid2">
            <Field label="Inventory" value={form.inventory} onChange={(v) => setForm({ ...form, inventory: v })} />
            <Field label="Weight (oz)" value={form.weightOz} onChange={(v) => setForm({ ...form, weightOz: v })} />
          </div>
          <div className="grid2">
            <Field label="Tag" value={form.tag} onChange={(v) => setForm({ ...form, tag: v })} />
            <Field label="Sort order" value={form.sortOrder} onChange={(v) => setForm({ ...form, sortOrder: v })} />
          </div>
          <Field label="Slug (optional)" value={form.slug} onChange={(v) => setForm({ ...form, slug: v })} />
          <div className="grid2">
            <label className="fld">
              <span>Status</span>
              <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value as "draft" | "published" })}>
                <option value="draft">draft</option>
                <option value="published">published</option>
              </select>
            </label>
            <label className="fld checkbox">
              <input type="checkbox" checked={form.featured} onChange={(e) => setForm({ ...form, featured: e.target.checked })} />
              <span style={{ marginLeft: 8 }}>Featured</span>
            </label>
          </div>
          {err && <p className="err">{err}</p>}
          <div className="actions">
            <button type="submit" className="btn-woven" disabled={busy}>
              {busy ? "Saving…" : "Save"}
            </button>
            <button type="button" className="btn-ghost" onClick={() => setEditing(null)}>
              Cancel
            </button>
          </div>
          <p className="note">Images are managed via Media (kept as-is on save here).</p>
        </form>
      ) : !loaded ? (
        <p style={{ fontFamily: fonts.body, color: `${colors.navy}aa` }}>Loading…</p>
      ) : (
        <>
          <AdminTableControls
            search={search}
            onSearchChange={setSearch}
            placeholder="Search products, brands, tags..."
            totalCount={list.length}
            resultCount={tableRows.length}
          >
            <AdminTableFilterSelect
              label="Status"
              value={statusFilter}
              onChange={setStatusFilter}
              options={[
                { value: "all", label: "All statuses" },
                { value: "draft", label: "Draft" },
                { value: "published", label: "Published" },
              ]}
            />
            <AdminTableFilterSelect
              label="Category"
              value={categoryFilter}
              onChange={setCategoryFilter}
              options={categoryOptions}
            />
          </AdminTableControls>
          {list.length === 0 ? (
            <p style={{ fontFamily: fonts.body, color: `${colors.navy}aa` }}>No products yet.</p>
          ) : tableRows.length === 0 ? (
            <p style={{ fontFamily: fonts.body, color: `${colors.navy}aa` }}>No products match those filters.</p>
          ) : (
            <table className="paper-table">
              <thead>
                <tr>
                  <SortableTh label="Name" sortKey="name" sort={sort} onSort={requestSort} />
                  <SortableTh label="Brand" sortKey="brand" sort={sort} onSort={requestSort} />
                  <SortableTh label="Price" sortKey="price" sort={sort} onSort={requestSort} />
                  <SortableTh label="Stock" sortKey="stock" sort={sort} onSort={requestSort} />
                  <SortableTh label="Status" sortKey="status" sort={sort} onSort={requestSort} />
                  <th />
                </tr>
              </thead>
              <tbody>
                {tableRows.map((p) => (
                  <tr key={p.id}>
                    <td>
                      <strong>{p.name}</strong>
                      <span className="slug">{p.slug}</span>
                    </td>
                    <td>{p.brand}</td>
                    <td>{formatMoney(p.priceCents)}</td>
                    <td>{formatInteger(p.inventory)}</td>
                    <td>{p.status}</td>
                    <td style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                      <button type="button" className="btn-ghost sm" onClick={() => startEdit(p)}>
                        Edit
                      </button>
                      <button type="button" className="btn-danger sm" onClick={() => remove(p)}>
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </>
      )}
    </section>
  );
}

function Field({
  label,
  value,
  onChange,
  required,
  textarea,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  required?: boolean;
  textarea?: boolean;
}) {
  return (
    <label className="fld">
      <span>{label}</span>
      {textarea ? (
        <textarea value={value} onChange={(e) => onChange(e.target.value)} required={required} />
      ) : (
        <input type="text" value={value} onChange={(e) => onChange(e.target.value)} required={required} />
      )}
    </label>
  );
}

const css = `
  .hdr { display: flex; align-items: center; justify-content: space-between; gap: 1rem; margin-bottom: 28px; }
  .btn-woven {
    display: inline-flex; align-items: center; gap: 7px; padding: 10px 20px;
    background: ${colors.berryRose}; color: #fff; font-family: ${fonts.body};
    font-size: 0.7rem; font-weight: 700; letter-spacing: 0.06em; text-transform: uppercase;
    border: none; border-radius: 7px; cursor: pointer;
    outline: 2px dashed rgba(255,255,255,0.45); outline-offset: -5px; box-shadow: 0 2px 8px rgba(212,96,106,0.30);
  }
  .btn-woven:hover { background: #c0535d; }
  .btn-woven:disabled { opacity: 0.6; cursor: default; }
  .btn-ghost {
    background: ${colors.cream}; border: 1px solid rgba(201,147,76,0.55); color: ${colors.navy};
    font-family: ${fonts.body}; font-size: 0.7rem; font-weight: 700; letter-spacing: 0.06em; text-transform: uppercase;
    border-radius: 7px; padding: 9px 16px; cursor: pointer;
  }
  .btn-ghost:hover { box-shadow: 0 6px 14px -8px rgba(8,45,61,0.35); }
  .btn-danger { background: ${colors.cream}; border: 1px solid ${colors.berryRose}; color: ${colors.berryRose};
    font-family: ${fonts.body}; font-size: 0.7rem; font-weight: 700; letter-spacing: 0.06em; text-transform: uppercase;
    border-radius: 7px; padding: 9px 16px; cursor: pointer; }
  .btn-danger:hover { background: ${colors.berryRose}; color: #fff; }
  .sm { padding: 6px 12px; margin-left: 6px; font-size: 0.62rem; }

  .paper-table { width: 100%; border-collapse: separate; border-spacing: 0; background: #fff; border: 1.5px dashed rgba(201,147,76,0.40); border-radius: 16px; overflow: hidden; }
  .paper-table th, .paper-table td { text-align: left; padding: 0.7rem 0.9rem; border-bottom: 1px dashed rgba(201,147,76,0.18); font-family: ${fonts.body}; vertical-align: middle; }
  .paper-table thead th { background: ${colors.blushLight}; font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.18em; color: rgba(8,45,61,0.7); border-bottom: 1px solid rgba(201,147,76,0.15); }
  .paper-table tbody tr:last-child td { border-bottom: none; }
  .paper-table tbody td { font-size: 13.5px; color: rgba(8,45,61,0.85); }
  .paper-table .slug { display: block; font-size: 11px; color: rgba(8,45,61,0.47); }

  .paper-card { background: #fff; border: 1.5px dashed rgba(201,147,76,0.40); border-radius: 16px; padding: 1.5rem 1.75rem; }
  .form-legend { font-family: ${fonts.logo}; font-weight: 500; font-size: 1.2rem; color: ${colors.navy}; display: block; margin-bottom: 1.25rem; }
  .grid2 { display: grid; grid-template-columns: 1fr 1fr; gap: 1rem; }
  .fld { display: block; margin-bottom: 1rem; }
  .fld > span { display: block; font-family: ${fonts.body}; font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.1em; color: rgba(8,45,61,0.65); margin-bottom: 0.35rem; }
  .fld input[type=text], .fld select {
    width: 100%; padding: 0.55rem 0.9rem; border: 1px solid rgba(201,147,76,0.40); border-radius: 999px;
    background: ${colors.cream}; color: ${colors.navy}; font-family: ${fonts.body}; font-weight: 600; font-size: 13.5px; outline: none;
  }
  .fld textarea {
    width: 100%; min-height: 90px; padding: 0.7rem 0.9rem; border: 1px solid rgba(201,147,76,0.40); border-radius: 12px;
    background: ${colors.cream}; color: ${colors.navy}; font-family: ${fonts.body}; font-weight: 500; font-size: 13.5px; outline: none;
  }
  .fld input:focus, .fld select:focus, .fld textarea:focus { border-color: ${colors.berryRose}; box-shadow: 0 4px 14px -8px rgba(212,96,106,0.55); }
  .fld.checkbox { display: flex; align-items: center; padding-top: 1.6rem; }
  .fld.checkbox input { accent-color: ${colors.berryRose}; }
  .actions { display: flex; gap: 0.6rem; margin-top: 0.5rem; }
  .err { color: ${colors.berryRose}; font-family: ${fonts.body}; font-weight: 600; font-size: 0.85rem; margin: 0.5rem 0; }
  .note { font-family: ${fonts.body}; font-size: 0.75rem; color: rgba(8,45,61,0.5); margin-top: 0.75rem; }
`;
